import { useEffect, useState } from "react";
import { Loader2, X, CheckCircle2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/*
 * ScanPreviewModal — side-by-side preview + inline edit for a scanned résumé
 * or employment-application form.
 *
 * Flow:
 *   1. Parent passes the picked File[] + the scan `type` ("resume" | "application").
 *   2. We POST to /scan?dry_run=true → server runs OCR, returns parsed fields
 *      + a base64 thumbnail of page 1.
 *   3. Recruiter edits any field on the right pane.
 *   4. Save → we POST the SAME files again with edited_fields_json so the
 *      backend skips the OCR call and just persists. Parent gets the new row
 *      via the onCommitted callback.
 */
export default function ScanPreviewModal({ jobId, files, type, onClose, onCommitted }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [edited, setEdited] = useState({
    candidate_name: "",
    candidate_email: "",
    candidate_phone: "",
    candidate_address: "",
    text: "",
  });

  useEffect(() => {
    let cancelled = false;
    const url =
      type === "application"
        ? `/jobs/${jobId}/applications/scan?dry_run=true`
        : `/jobs/${jobId}/resumes/scan?dry_run=true`;
    (async () => {
      try {
        const fd = new FormData();
        for (const f of files) fd.append("files", f, f.name);
        const { data } = await api.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (cancelled) return;
        setPreview(data);
        setEdited({
          candidate_name: data.parsed?.candidate_name || "",
          candidate_email: data.parsed?.candidate_email || "",
          candidate_phone: data.parsed?.candidate_phone || "",
          candidate_address: data.parsed?.candidate_address || "",
          text: data.text || "",
        });
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Scan failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files, jobId, type]);

  const handleCommit = async () => {
    setSubmitting(true);
    setError("");
    const url =
      type === "application"
        ? `/jobs/${jobId}/applications/scan`
        : `/jobs/${jobId}/resumes/scan`;
    try {
      const editedPayload = {
        candidate_name: edited.candidate_name,
        candidate_email: edited.candidate_email,
        candidate_phone: edited.candidate_phone,
        candidate_address: edited.candidate_address,
        text: edited.text,
        // Persist the confidence dict from the dry-run preview so the admin
        // scan-quality KPI can compute "% with ≥1 low-confidence field".
        field_confidence: preview?.field_confidence || {},
      };
      if (type === "application" && preview?.form) {
        editedPayload.form = preview.form;
      }
      const fd = new FormData();
      for (const f of files) fd.append("files", f, f.name);
      fd.append("edited_fields_json", JSON.stringify(editedPayload));
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCommitted(data);
      toast.success(`Saved · scored ${data.score}/100`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
      setSubmitting(false);
    }
  };

  const fieldClass =
    "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500";
  const labelClass =
    "block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-700";

  const confFor = (field) => preview?.field_confidence?.[field] || "";

  // Soft-amber border on uncertain fields, soft-rose on low-confidence ones.
  // High-confidence fields stay neutral so the recruiter's eye is drawn to
  // what actually needs review.
  const fieldClassFor = (field) => {
    const c = confFor(field);
    if (c === "low") {
      return `${fieldClass} border-rose-300 bg-rose-50/40 focus:border-rose-500`;
    }
    if (c === "medium") {
      return `${fieldClass} border-amber-300 bg-amber-50/40 focus:border-amber-500`;
    }
    return fieldClass;
  };

  const confBadge = (field) => {
    const c = confFor(field);
    if (!c || c === "high") return null;
    const cls =
      c === "low"
        ? "bg-rose-100 text-rose-700 border-rose-200"
        : "bg-amber-100 text-amber-800 border-amber-200";
    return (
      <span
        data-testid={`scan-preview-conf-${field}`}
        title={
          c === "low"
            ? "AI flagged this field as LOW confidence — please double-check"
            : "AI flagged this field as MEDIUM confidence"
        }
        className={`ml-2 inline-flex items-center border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] ${cls}`}
      >
        {c === "low" ? "Verify" : "Uncertain"}
      </span>
    );
  };

  return (
    <div
      data-testid="scan-preview-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
              Preview & edit ·{" "}
              {type === "application" ? "Employment Application" : "Résumé"}
            </div>
            <h2 className="font-heading text-lg font-semibold">
              Correct any OCR mistakes before saving
            </h2>
          </div>
          <button
            type="button"
            data-testid="scan-preview-close"
            onClick={onClose}
            disabled={submitting}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {loading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-slate-500">
            <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
            <div className="text-sm">
              Running AI vision OCR on {files.length} page
              {files.length === 1 ? "" : "s"}…
            </div>
            <div className="text-xs">Usually takes 4–8 seconds per page</div>
          </div>
        ) : error && !preview ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="max-w-md border border-rose-200 bg-rose-50 p-5 text-rose-800">
              <AlertTriangle className="mb-2 h-5 w-5" />
              <h3 className="font-semibold">Scan failed</h3>
              <p className="mt-1 text-sm">{error}</p>
            </div>
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-2">
            {/* Left: image preview */}
            <div
              data-testid="scan-preview-image-pane"
              className="overflow-auto border-r border-slate-200 bg-slate-50 p-4"
            >
              {preview?.thumbnail_b64 && (
                <img
                  data-testid="scan-preview-image"
                  src={`data:${preview.thumbnail_mime};base64,${preview.thumbnail_b64}`}
                  alt="Scanned page 1"
                  className="mx-auto h-auto w-full max-w-md border border-slate-300 shadow-sm"
                />
              )}
              {preview?.scan_pages > 1 && (
                <p className="mt-3 text-center text-[11px] text-slate-500">
                  Page 1 of {preview.scan_pages} ·
                  Other pages used for OCR but not shown here
                </p>
              )}
            </div>

            {/* Right: editable fields */}
            <div className="flex flex-col overflow-hidden">
              <div className="flex-1 space-y-4 overflow-auto p-6">
                <div className="border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                  <strong>AI scored this {preview?.score || preview?.parsed?.score}/100</strong>{" "}
                  for the job. Edit any field below — the row will be re-scored
                  when you save.
                </div>
                {Object.values(preview?.field_confidence || {}).some(
                  (v) => v === "low" || v === "medium"
                ) && (
                  <div
                    data-testid="scan-preview-conf-hint"
                    className="border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
                  >
                    Fields with a coloured border were flagged by the AI for
                    review — <strong>amber</strong> = uncertain,{" "}
                    <strong>rose</strong> = low confidence. Double-check those
                    before saving.
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>
                      Candidate name
                      {confBadge("candidate_name")}
                    </span>
                    <input
                      data-testid="scan-preview-name"
                      value={edited.candidate_name}
                      onChange={(e) =>
                        setEdited({ ...edited, candidate_name: e.target.value })
                      }
                      className={fieldClassFor("candidate_name")}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>
                      Email
                      {confBadge("candidate_email")}
                    </span>
                    <input
                      data-testid="scan-preview-email"
                      type="email"
                      value={edited.candidate_email}
                      onChange={(e) =>
                        setEdited({ ...edited, candidate_email: e.target.value })
                      }
                      className={fieldClassFor("candidate_email")}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>
                      Phone
                      {confBadge("candidate_phone")}
                    </span>
                    <input
                      data-testid="scan-preview-phone"
                      value={edited.candidate_phone}
                      onChange={(e) =>
                        setEdited({ ...edited, candidate_phone: e.target.value })
                      }
                      className={fieldClassFor("candidate_phone")}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>
                      Address
                      {confBadge("candidate_address")}
                    </span>
                    <input
                      data-testid="scan-preview-address"
                      value={edited.candidate_address}
                      onChange={(e) =>
                        setEdited({
                          ...edited,
                          candidate_address: e.target.value,
                        })
                      }
                      className={fieldClassFor("candidate_address")}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className={labelClass}>
                    OCR&apos;d text (edit to fix illegible handwriting)
                  </span>
                  <textarea
                    data-testid="scan-preview-text"
                    value={edited.text}
                    onChange={(e) => setEdited({ ...edited, text: e.target.value })}
                    rows={12}
                    className={`${fieldClass} font-mono text-[12px]`}
                  />
                </label>
                {error && (
                  <div
                    data-testid="scan-preview-error"
                    className="border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                  >
                    {error}
                  </div>
                )}
              </div>
              <footer className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
                <button
                  type="button"
                  data-testid="scan-preview-discard"
                  onClick={onClose}
                  disabled={submitting}
                  className="border border-slate-300 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 hover:bg-slate-100"
                >
                  Discard
                </button>
                <button
                  type="button"
                  data-testid="scan-preview-save"
                  onClick={handleCommit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-blue-600 px-5 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save applicant
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
